import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { request, requestV3 } from './customAxios'
import config from './config'
import { getErrorMessageErrorResponse } from './utils'
import { ErrorResponse } from '../types/api-error'
import { captureEvent, EVENTS } from '@/lib/posthog-events'

interface ApiRequestParams {
  url: string
  method: string
  data: unknown
  isAuthenticated: boolean
  fallbackError: string
  onError?: (error: unknown) => void
  continueOnError?: boolean
  isAbsoluteUrl?: boolean
  sendCredentials?: boolean
  isV3Api?: boolean
  headers?: AxiosRequestConfig['headers']
  useGuestToken?: boolean
}

const REQUEST_TIMEOUT_MS = 15_000 //15seconds
const MAX_TRANSIENT_RETRIES = 2
const RETRYABLE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])
const RETRYABLE_STATUS_CODES = new Set([408, 429])

const wait = (delayMs: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, delayMs))

const isBrowserOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false

const isCancelledRequest = (error: unknown): boolean =>
  axios.isCancel(error) ||
  (axios.isAxiosError(error) && error.code === AxiosError.ERR_CANCELED)

const isRetryableError = (error: unknown, method: string) => {
  if (!axios.isAxiosError(error) || isCancelledRequest(error) || isBrowserOffline()) {
    return false
  }

  if (!RETRYABLE_METHODS.has(method.toUpperCase())) return false

  const status = error.response?.status
  return (
    !status ||
    RETRYABLE_STATUS_CODES.has(status) ||
    status >= 500
  )
}

const getRetryDelay = (retryNumber: number) => {
  const exponentialDelay = 500 * 2 ** (retryNumber - 1)
  const jitter = Math.floor(Math.random() * 250)
  return exponentialDelay + jitter
}

const CLIENT_ID_COOKIE = "client_id";

const getOrCreateClientId = () => {
  if (typeof document === "undefined") {
    return "";
  }
  const match = document.cookie.match(
    new RegExp("(^| )" + CLIENT_ID_COOKIE + "=([^;]+)")
  );
  if (match) {
    return decodeURIComponent(match[2]);
  }
  const id = crypto.randomUUID();

  const secure =
    typeof window !== "undefined" &&
    window.location.protocol === "https:";
  document.cookie = `${CLIENT_ID_COOKIE}=${encodeURIComponent(
    id
  )}; Path=/; Max-Age=31536000; SameSite=Lax${secure ? "; Secure" : ""}`;
  return id;
}


export const makeApiRequest = async (params: ApiRequestParams) => {
  const requestFn = params.isAuthenticated ? (params.isV3Api ? requestV3 : request) : axios.request
  // Compute final URL outside try/catch so it is available in catch
  const finalUrl = ((params.isAbsoluteUrl || params.isAuthenticated) && !params.isV3Api)
    ? params.url
    : params.isV3Api ? config.getBackendUrlV3(params.url) : config.getBackendUrl(params.url)

  const startedAt = Date.now()
  let retryCount = 0

  try {
    let response

    while (true) {
      try {
        response = await requestFn({
          method: params.method,
          url: finalUrl,
          data: params.data,
          timeout: REQUEST_TIMEOUT_MS,
          withCredentials: true,
          // withCredentials:
          //   params.sendCredentials || (params.isAuthenticated ? false : true),
          headers: {
            "X-Client-Id": getOrCreateClientId(),
            ...(params.headers ?? {}),
          }
        })
        break
      } catch (error: unknown) {
        if (
          retryCount >= MAX_TRANSIENT_RETRIES ||
          !isRetryableError(error, params.method)
        ) {
          throw error
        }

        retryCount += 1
        await wait(getRetryDelay(retryCount))
      }
    }

    const hasRespCode = Object.prototype.hasOwnProperty.call(
      response.data ?? {},
      'respCode'
    )

    if (hasRespCode && response.data.respCode == "-1" && params.fallbackError === '') {
      // Application-level error response
      captureEvent(EVENTS.ERROR_API_RESPONSE, {
        url: finalUrl,
        method: params.method,
        resp_code: response.data.respCode,
        message: response.data.message,
        api_version: params.isV3Api ? 'v3' : 'v2',
        authenticated: params.isAuthenticated,
      })
      throw new Error(response.data.message)
    }
    if (hasRespCode && response.data.respCode != 1 && params.fallbackError !== 'noFallback') {
      captureEvent(EVENTS.ERROR_API_RESPONSE, {
        url: finalUrl,
        method: params.method,
        resp_code: response.data.respCode,
        fallback_error: params.fallbackError,
        api_version: params.isV3Api ? 'v3' : 'v2',
        authenticated: params.isAuthenticated,
      })
      throw new Error(params.fallbackError)
    }

    return response.data
  } catch (e: unknown) {

    if (params.onError) {
      params.onError(e)
      if (params.continueOnError) {
        return
      }
    }
    if (isCancelledRequest(e)) {
      throw e
    }
    if (axios.isAxiosError(e)) {
      const status = e.response?.status
      const eventName = status
        ? EVENTS.ERROR_API_RESPONSE
        : EVENTS.ERROR_NETWORK_REQUEST

      captureEvent(eventName, {
        url: e.config?.url || finalUrl,
        method: e.config?.method || params.method,
        status,
        code: e.code,
        message: e.message,
        api_version: params.isV3Api ? 'v3' : 'v2',
        authenticated: params.isAuthenticated,
        retry_count: retryCount,
        duration_ms: Date.now() - startedAt,
        online: !isBrowserOffline(),
        error_type: status
          ? status >= 500
            ? 'server_error'
            : 'http_error'
          : isBrowserOffline()
            ? 'offline'
            : e.code === AxiosError.ECONNABORTED
              ? 'timeout'
              : 'network_error',
      })
      if (params.fallbackError === '' && e.response?.data?.message) {
        throw new Error(e.response.data.message)
      }
      throw new Error(
        getErrorMessageErrorResponse(
          e.response?.data as ErrorResponse,
          params.fallbackError
        )
      )
    }
    if (e instanceof Error) {
      throw new Error(e?.message)
    }
    throw new Error(params?.fallbackError)
  }
}
